<template>
  <div class="box">
    <h1 class="title is-4 has-text-primary">
      Facturas Canceladas
    </h1>
    <AppPagination
      class="is-centered"
      :current-page="currentPage"
      :buttons-max="buttonsMax"
      :pages-total="totalPages"
      @change="goToPage"
    />
    <table class="table is-fullwidth">
      <thead>
        <tr>
          <th>Folio</th>
          <th>Fecha</th>
          <th>RFC Receptor</th>
          <th>Razón social Cliente</th>
          <th>Uuid</th>
          <th>Total</th>
          <th>Tipo</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr v-if="isLoading">
          <td
            colspan="8"
            class="is-loading"
          >
            <a class="button is-loading is-fullwidth">
              Cargando..
            </a>
          </td>
        </tr>
        <template v-else>
          <tr
            v-if="invoices.length === 0"
          >
            <td
              class="empty"
              colspan="7"
            >
              Sin datos
            </td>
          </tr>
          <tr
            v-for="(invoice, index) in invoices"
            v-else
            :key="index"
          >
            <td>
              {{ invoice.voucherSeries }}
              -
              {{ invoice.voucherConsecutive }}
            </td>
            <td>{{ invoice.voucherDate | toHumanDate }}</td>
            <td>{{ invoice.receiverRfc }}</td>
            <td>{{ invoice.receiverName }}</td>
            <td>{{ getUuid(invoice) }}</td>
            <td>{{ invoice.voucherTotal | toCurrency(invoice.voucherCurrency) }}</td>
            <td>{{ voucherTypeLabel(invoice.voucherType) }}</td>
            <td>
              <div
                v-if="invoice.voucherType === 'P'"
                class="actions"
              >
                <AppRadialMenu
                  :size="36"
                  :item-size="48"
                  :radius="80"
                  :angle-restriction="50"
                  :rotate="-25"
                >
                  <AppRadialMenuItem
                    class="tooltip is-tooltip-primary has-background-primary has-text-white"
                    data-tooltip="Detalle de documento"
                  >
                    <RouterLink
                      class="action-option"
                      :to="{ name: getRoute(invoice), params: { uuid: invoice.id } }"
                    >
                      <BIcon
                        icon="eye"
                        class="action-option-icon"
                      />
                    </RouterLink>
                  </AppRadialMenuItem>
                  <AppRadialMenuItem
                    v-if="toRep(invoice)"
                    class="tooltip is-tooltip-success has-background-success has-text-white"
                    data-tooltip="Reemitir rep"
                  >
                    <RouterLink
                      class="action-option"
                      :to="toRep(invoice)"
                    >
                      <BIcon
                        icon="undo-variant"
                        class="action-option-icon"
                      />
                    </RouterLink>
                  </AppRadialMenuItem>
                </AppRadialMenu>
              </div>
              <div
                v-if="invoice.voucherType !== 'P'"
                class="buttons"
              >
                <RouterLink
                  class="button is-info tooltip is-tooltip-info"
                  data-tooltip="Detalle de documento"
                  :to="{ name: getRoute(invoice), params: { uuid: invoice.id } }"
                >
                  <BIcon icon="eye-off" />
                </RouterLink>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <AppPagination
      class="is-centered"
      :current-page="currentPage"
      :buttons-max="buttonsMax"
      :pages-total="totalPages"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import { voucherType as voucherTypeCatalog } from '@machete/common-catalogs';

import AppPagination from '@/components/AppPagination.vue';

export default {
  name: 'CanceledList',
  components: {
    AppRadialMenu: () => import(/* webpackChunkName: "app-radial-menu" */ '@/components/AppRadialMenu.vue'),
    AppRadialMenuItem: () => import(/* webpackChunkName: "app-radial-menu-item" */ '@/components/AppRadialMenuItem.vue'),
    AppPagination,
  },
  data() {
    return {
      isLoading: false,
      buttonsMax: 5,
    };
  },
  computed: {
    ...mapState({
      total: state => state.invoices.total,
      totalPages: state => state.invoices.totalPages,
    }),
    ...mapGetters({
      invoices: 'invoices/cleanInvoices',
    }),
    voucherTypeLabel() {
      return (key) => {
        const voucherType = voucherTypeCatalog.find(type => type.key === key);

        return voucherType.value;
      };
    },
    lastPage() {
      return Math.ceil(this.invoices.length / this.itemsPerPage);
    },
    currentPage() {
      return parseInt(this.$route.query.page, 10);
    },
    size() {
      return this.$route.query.size;
    },
    toRep() {
      return (invoice) => {
        if (invoice.voucherType === 'P') {
          return {
            name: 'NewRep',
            query: {
              replace: invoice.complement.digitalTaxStamp.uuid,
            },
          };
        } return null;
      };
    },
  },
  watch: {
    buttonsMax(newValue, oldValue) {
      if (newValue % 2) {
        return;
      }
      if (newValue < oldValue) {
        this.buttonsMax = newValue - 1;
      }
      if (newValue > oldValue) {
        this.buttonsMax = newValue + 1;
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    if (to.query.page) next();
    else {
      next({ name: to.name, query: { page: 1, size: 20 } });
    }
  },
  async beforeRouteUpdate(to, from, next) {
    const { page = 1, size = 20 } = to.query;

    await this.getAll({
      filter: 'canceled',
      page,
      size,
    });

    next({ query: { page, size } });
  },
  async beforeMount() {
    this.isLoading = true;
    await this.getAll({
      filter: 'canceled',
      page: this.currentPage,
      size: this.size,
    });
    this.isLoading = false;
  },
  methods: {
    ...mapActions({
      getAll: 'invoices/getAll',
    }),
    goToPage(page) {
      const { size = 20 } = this.$route.query;

      const query = {
        page,
        size,
      };

      this.$router.push({ query });
    },
    getRoute(invoice) {
      if (invoice.operation.isSalaryReceipt) return 'SalaryReceiptDetail';
      if (invoice.operation.isSaleNote) return 'SaleNoteDetail';
      switch (invoice.voucherType) {
        case 'E':
          return 'CreditNoteDetail';
        case 'N':
          return 'PayrollDetail';
        case 'P':
          return 'RepDetail';
        default:
          return 'InvoiceDetail';
      }
    },
    getUuid(invoice) {
      if (invoice.operation.isSalaryReceipt || invoice.operation.isSaleNote) {
        return '';
      }

      return invoice.complement.digitalTaxStamp.uuid;
    },
    checkLastPage() {
      if (this.currentPage < this.lastPage) {
        this.$router.push({ query: { page: this.lastPage } });
      }
    },
  },
};
</script>

<style scoped>
.empty {
  vertical-align: center;
  text-align: center;
}
.action-option {
  display: flex!important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
